import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useMemo } from 'react';
import { onboardingOutlinedInputRootSx } from '../Form/formFieldSpec';
import { OnboardingSelectionCounter } from './OnboardingSelectionCounter';
import { useOnboardingLens } from './onboardingLens';

interface OnboardingFormMultiSelectProps {
  id: string;
  label: string;
  value: string[];
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (value: string[]) => void;
  placeholder: string;
  error?: string;
  helperText?: string;
  maxSelections?: number;
  showCounter?: boolean;
  required?: boolean;
  disabled?: boolean;
  /** Months/options disabled because they are selected in a paired field. */
  excludedValues?: readonly string[];
}

function formatOnboardingMultiSelectDisplayValue(
  selectedValues: string[],
  options: ReadonlyArray<{ value: string; label: string }>,
  maxVisibleLabels: number,
): string | null {
  const selected = selectedValues.filter((item) => item.length > 0);
  if (selected.length === 0) {
    return null;
  }

  const labelByValue = new Map(options.map((option) => [option.value, option.label]));
  const labels = selected.map((item) => labelByValue.get(item) ?? item);
  const visibleLabels =
    labels.length > maxVisibleLabels
      ? `${labels.slice(0, maxVisibleLabels).join(', ')}...`
      : labels.join(', ');

  return `(${labels.length}) ${visibleLabels}`;
}

export function OnboardingFormMultiSelect({
  id,
  label,
  value,
  options,
  onChange,
  placeholder,
  error,
  helperText,
  maxSelections,
  showCounter = false,
  required = true,
  disabled = false,
  excludedValues = [],
}: OnboardingFormMultiSelectProps) {
  const { layout, typography, colors, focusRing, accentRedRequired } = useOnboardingLens();
  const { formField, multiSelect } = layout;
  const atMax = maxSelections !== undefined && value.length >= maxSelections;

  const selectRootSx = useMemo(
    () => ({
      height: formField.inputHeight,
      borderRadius: formField.inputRadius,
      ...onboardingOutlinedInputRootSx,
      '&&.Mui-focused': { boxShadow: focusRing },
    }),
    [focusRing, formField.inputHeight, formField.inputRadius],
  );

  const selectValue = useMemo(() => ['', ...value], [value]);

  const displayValue = useMemo(
    () => formatOnboardingMultiSelectDisplayValue(selectValue, options, multiSelect.maxVisibleLabels),
    [selectValue, options, multiSelect.maxVisibleLabels],
  );

  const selectPadding = {
    pt: formField.inputPaddingY,
    pr: `calc(${formField.inputPaddingX} + ${multiSelect.selectIconGutter})`,
    pb: formField.inputPaddingY,
    pl: formField.inputPaddingX,
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const nextValue =
      typeof event.target.value === 'string'
        ? event.target.value.split(',')
        : event.target.value;
    const filtered = nextValue.filter((item) => item.length > 0);
    if (maxSelections !== undefined && filtered.length > maxSelections) {
      return;
    }
    onChange(filtered);
  };

  return (
    <FormControl error={Boolean(error)} fullWidth sx={{ width: '100%', minWidth: 0, overflow: 'visible' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: formField.labelToInputGap,
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: formField.labelToInputGap,
            width: '100%',
            minHeight: formField.labelRowHeight,
            overflow: 'visible',
          }}
        >
          <Box
            component="label"
            htmlFor={id}
            sx={{
              display: 'flex',
              flex: '1 1 auto',
              alignItems: 'flex-start',
              gap: formField.labelGap,
              minWidth: 0,
              minHeight: formField.labelRowHeight,
            }}
          >
            <Typography component="span" sx={{ ...typography.labelEmphasis, color: colors.typeDefault }}>
              {label}
            </Typography>
            {required ? (
              <Typography component="span" sx={{ ...typography.labelRequired, color: accentRedRequired }}>
                *
              </Typography>
            ) : null}
          </Box>
          {showCounter && maxSelections !== undefined && (
            <OnboardingSelectionCounter current={value.length} max={maxSelections} />
          )}
        </Box>

        {helperText ? (
          <Typography
            sx={{
              ...typography.body,
              color: colors.typeMuted,
              mt: -0.5,
            }}
          >
            {helperText}
          </Typography>
        ) : null}

        <Select
          id={id}
          multiple
          displayEmpty
          disabled={disabled}
          input={<OutlinedInput notched={false} />}
          value={selectValue}
          onChange={handleChange}
          sx={{
            width: '100%',
            maxWidth: '100%',
            ...selectRootSx,
            '& .MuiOutlinedInput-root': { width: '100%' },
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              height: formField.inputHeight,
              minHeight: 'unset',
              p: selectPadding,
              ...typography.body,
              color: value.length > 0 ? colors.typeDefault : colors.typeMuted,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '& .MuiSelect-icon': {
              right: formField.inputPaddingX,
              width: layout.icon.md,
              height: layout.icon.md,
              color: colors.typeDefault,
            },
          }}
          renderValue={() =>
            displayValue ? (
              <Box
                component="span"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {displayValue}
              </Box>
            ) : (
              <Box
                component="span"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  color: colors.typeMuted,
                }}
              >
                {placeholder}
              </Box>
            )
          }
        >
          {options.map((option) => {
            const isSelected = value.includes(option.value);
            const isExcluded = excludedValues.includes(option.value);
            const isDisabled = (atMax && !isSelected) || isExcluded;

            return (
              <MenuItem key={`${id}-${option.value}`} value={option.value} disabled={isDisabled}>
                <Checkbox checked={isSelected} disabled={isDisabled} />
                <ListItemText primary={option.label} />
              </MenuItem>
            );
          })}
        </Select>
      </Box>
      {error && <FormHelperText sx={{ mx: 0, mt: layout.formField.labelGap }}>{error}</FormHelperText>}
    </FormControl>
  );
}
